/**
 * Frame-coalesced scheduling with a timeout fallback.
 *
 * Animation frames are the right pacing signal while the user drags a slider: many
 * pointer events collapse into one rebuild per frame. But rAF stops firing whenever
 * the page is not compositing — a background tab, a hidden pane, an offscreen
 * embed — and work scheduled that way would simply never happen. The timeout is the
 * safety net: whichever fires first wins, the other is cancelled.
 */
export interface ScheduledTask {
  cancel(): void;
}

export function scheduleFrame(fn: () => void, fallbackMs = 120): ScheduledTask {
  let done = false;

  const run = (): void => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    cancelAnimationFrame(raf);
    fn();
  };

  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : 0;
  const timer = setTimeout(run, fallbackMs);

  return {
    cancel() {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    },
  };
}
